import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumb, Empty, message, Tag, Button, Divider } from "antd";
import { useDevice } from "../../hooks";
import { getAllBlog, getBySlug } from "../../api/blog";
// import ItemBlog from "../../section/itemBlog"; // We'll build a custom simple recent section
import dayjs from "dayjs";
import { ArrowLeftOutlined, ArrowRightOutlined, CalendarOutlined, ClockCircleOutlined, FacebookOutlined, ShareAltOutlined, TwitterOutlined } from "@ant-design/icons";

export default function BlogDetail() {
    const param = useParams();
    const { isMobile } = useDevice();
    const navigate = useNavigate();
    const [blog, setBlog] = useState([]);
    const [allBlog, setAllBlog] = useState();
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 4, // Just a few for the sidebar/bottom
    });

    useEffect(()=> {        
        const query = `page=${pagination?.page}&pageSize=${pagination?.pageSize}`;
        getAllBlog(query, setAllBlog);
    }, [pagination])

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [param?.slug]); 

    useEffect(() => {
        getBySlug(param?.slug, setBlog, message);
    }, [param?.slug]);

    const post = blog?.data?.[0];

    useEffect(() => {
        if (post) {
            document.title = post.blog_title_seo || post.blog_name;
            let metaDescription = document.querySelector('meta[name="description"]');
            if (!metaDescription) {
                metaDescription = document.createElement('meta');
                metaDescription.name = 'description';
                document.head.appendChild(metaDescription);
            }
            const descValue = post.blog_description_seo || post.blog_description?.replace(/<[^>]*>/g, '').substring(0, 160) || "";
            metaDescription.setAttribute('content', descValue);
        }
    }, [post]);

    if (!post && blog?.data) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Empty description="Không tìm thấy bài viết" />
            </div>
        );
    }

    return (
        <div className={`min-h-screen bg-white pb-20 ${isMobile && ""}`}>
            {/* Minimalist Header for Readability */}
            <div className="bg-gray-50 border-b border-gray-100 py-6 mb-10">
                <div className="max-w-screen-2xl mx-auto px-4 md:px-10">
                    <Breadcrumb
                        items={[
                            { title: <a href="/">Trang chủ</a> },
                            { title: <a href="/blog">Blog</a> },
                            { title: <span className="text-gray-400 line-clamp-1">{post?.blog_name}</span> }
                        ]}
                    />
                </div>
            </div>

            <div className="max-w-screen-2xl mx-auto px-4 md:px-10">
                <div className="flex flex-col lg:flex-row gap-16">
                    {/* Main Content */}
                    <article className="flex-1 max-w-none">
                        <header className="mb-10">
                            <Tag color="blue" className="mb-6 rounded-full px-4 py-1 font-bold uppercase tracking-widest text-[10px]">
                                {post?.category_blog_name}
                            </Tag>
                            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-8 leading-tight tracking-tight">
                                {post?.blog_name}
                            </h1>
                            
                            <div className="flex flex-wrap items-center gap-6 text-gray-500 text-sm py-6 border-y border-gray-100">
                                <div className="flex items-center gap-2">
                                    <CalendarOutlined className="text-blue-500" />
                                    <span>{dayjs(post?.create_at).format("DD MMMM, YYYY")}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ClockCircleOutlined className="text-blue-500" />
                                    <span>5 phút đọc</span>
                                </div>
                                <div className="ml-auto flex items-center gap-4 text-gray-400">
                                    <span className="text-xs font-bold uppercase tracking-widest">Chia sẻ:</span>
                                    <FacebookOutlined className="text-xl cursor-pointer hover:text-blue-600 transition-colors" />
                                    <TwitterOutlined className="text-xl cursor-pointer hover:text-blue-400 transition-colors" />
                                    <ShareAltOutlined className="text-xl cursor-pointer hover:text-green-500 transition-colors" />
                                </div>
                            </div>
                        </header>

                        <div className="mb-12 rounded-[2.5rem] overflow-hidden shadow-2xl border border-gray-100">
                            <img 
                                src={post?.blog_image} 
                                alt={post?.blog_name} 
                                className="w-full h-auto object-cover max-h-[700px]"
                            />
                        </div>

                        <div
                            className="sun-editor-editable blog-content"
                            dangerouslySetInnerHTML={{ __html: post?.blog_description || "" }}
                        />

                        <Divider className="my-16" />

                        {/* Navigation between posts */}
                        <div className="flex justify-between items-center py-12 bg-gray-50 rounded-[2.5rem] px-10 border border-gray-100">
                            <div className="flex flex-col items-start">
                                <span className="text-xs font-black text-gray-400 mb-3 uppercase tracking-[0.2em]">Quay lại</span>
                                <Button 
                                    type="link" 
                                    onClick={() => navigate('/blog')}
                                    icon={<ArrowLeftOutlined />}
                                    className="p-0 h-auto text-blue-600 font-black text-xl hover:translate-x-[-8px] transition-transform flex items-center gap-2"
                                >
                                    Danh sách bài viết
                                </Button>
                            </div>
                        </div>
                    </article>

                    {/* Sidebar */}
                    <aside className="w-full lg:w-[400px] shrink-0">
                        <div className="sticky top-24">
                            <div className="bg-[#f8fafc] rounded-[2.5rem] p-10 border border-gray-100">
                                <h3 className="text-2xl font-black text-gray-900 mb-10 flex items-center gap-3">
                                    <span className="w-8 h-1.5 bg-blue-600 rounded-full"></span>
                                    Mới cập nhật
                                </h3>
                                
                                <div className="space-y-10">
                                    {allBlog?.data?.slice(0, 4).map((item, i) => (
                                        <a 
                                            key={i} 
                                            href={`/blog/${item?.blog_slug}`} 
                                            className="group flex gap-6 transition-all"
                                        >
                                            <div className="w-24 h-24 shrink-0 rounded-2xl overflow-hidden shadow-md">
                                                <img 
                                                    src={item?.blog_image} 
                                                    alt={item?.blog_name} 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                                                />
                                            </div>
                                            <div className="flex flex-col justify-center">
                                                <h4 className="text-base font-bold text-gray-800 line-clamp-2 group-hover:text-blue-600 transition-colors leading-tight mb-2">
                                                    {item?.blog_name}
                                                </h4>
                                                <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">{dayjs(item?.create_at).format("DD MMM, YYYY")}</span>
                                            </div>
                                        </a>
                                    ))}
                                </div>

                                <Button 
                                    block 
                                    size="large"
                                    className="mt-12 h-14 rounded-[1.2rem] font-black uppercase tracking-widest text-xs border-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm flex items-center justify-center gap-2"
                                    onClick={() => navigate('/blog')}
                                >
                                    Tất cả bài viết <ArrowRightOutlined />
                                </Button>
                            </div>

                            {/* Newsletter widget */}
                            <div className="mt-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2.5rem] p-10 text-white text-center shadow-2xl shadow-blue-200 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                                <h3 className="text-xl font-black mb-4 relative z-10">Nhận bản tin tuần</h3>
                                <p className="text-sm text-blue-100 mb-8 font-light relative z-10">Cập nhật kiến thức mới nhất mỗi tuần qua email.</p>
                                <input 
                                    type="text" 
                                    placeholder="Email của bạn"
                                    className="w-full bg-white/10 border border-white/20 rounded-2xl px-6 py-4 text-white placeholder:text-blue-100 text-sm mb-6 focus:outline-none focus:ring-2 focus:ring-white/50 relative z-10"
                                />
                                <Button block size="large" className="h-14 rounded-2xl bg-white text-blue-600 border-none font-black uppercase tracking-[0.2em] text-[10px] hover:bg-gray-100 transition-all shadow-lg relative z-10">
                                    Đăng Ký Ngay
                                </Button>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    )
}

